import { InputWithValidation } from 'acscp/components/common/validation/inputWithValidation';
import { Dispatch } from 'redux';
import { ContinueAsGuest, FetchRequest, FetchResetStatus } from 'acscp/actions';
import { BaseRequest, PasswordTokenRequest, RequestClass } from 'acscp/api/requests';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { AppState } from 'acscp/store';
import { Link } from 'react-router-dom';
import { defineMessages, FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import React from 'react';
import { Form, FormHeader } from 'acscp/components/common/form';
import formStyle from 'acscp/components/common/form.module.scss';
import loginPageFormStyle from './loginPageForms.module.scss';
import { ButtonWithLoadingIndicator } from 'acscp/components/common/buttonWithLoadingIndicator';
import { PasswordInputWithValidation } from 'acscp/components/common/validation/passwordInputWithValidation';
import { FormValidator, ValidationRule } from 'acscp/components/common/helpers/formValidator';
import { StringValidationChecks } from 'acscp/components/common/helpers/validationChecks';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { RequestErrorMessage } from 'acscp/components/common/requestErrorMessage';
import { Location } from 'history';
import { LoginPageVariant } from 'acscp/store/loginPageVariant';
import { encodeLocationForRedirect } from 'acscp/components/routeHelper';

const messages = defineMessages({
    emailError: {
        id: 'components.auth.loginForm.emailInvalid',
        defaultMessage: 'Please enter a valid email.'
    },
    passwordError: {
        id: 'components.auth.loginForm.passwordRequired',
        defaultMessage: 'Password is required.'
    },
    emailPlaceholder: {
        id: 'components.auth.loginForm.emailPlaceholder',
        defaultMessage: 'EMAIL ADDRESS'
    },
    passwordPlaceholder: {
        id: 'components.auth.loginForm.passwordPlaceholder',
        defaultMessage: 'PASSWORD'
    }
});

export enum ForgotPasswordLinkPlacement {
    Top,
    Bottom
}

interface LoginFormOwnProps {
    location: Location;
    loginPageVariant: LoginPageVariant;
    showLoginHeader: boolean;
    introText?: FormattedMessage.MessageDescriptor;
    forgotPasswordLinkPlacement: ForgotPasswordLinkPlacement;
    showContinueAsGuest: boolean;

    // Content that should be rendered within the form box; will be separated by a line from the rest of the login form
    underFormContent?: React.ReactNode;
}

type LoginFormStateProps = RequestStateProps;

interface LoginFormDispatchProps {
    login: (username: string, password: string) => void;
    loginAsGuest: () => void;
    resetLoginErrors: () => void;
}

interface LoginForm {
    username: string;
    password: string;
}

type LoginFormInternalState = LoginForm & {
    showErrors: boolean;
};

type LoginFormProps = LoginFormOwnProps & LoginFormStateProps & LoginFormDispatchProps & InjectedIntlProps;

export class LoginFormPresentational extends React.Component<LoginFormProps, LoginFormInternalState> {
    private validator: FormValidator;

    constructor(props: LoginFormProps) {
        super(props);

        this.validator = new FormValidator(this.getValidationRules());

        this.state = { username: '', password: '', showErrors: false };
    }

    private getValidationRules(): ValidationRule[] {
        const translator = this.props.intl.formatMessage;

        return [
            {
                validatedPropertyName: 'username',
                validationCheck: StringValidationChecks.isEmail(() => this.state.username),
                errorMessage: translator(messages.emailError)
            },
            {
                validatedPropertyName: 'password',
                validationCheck: StringValidationChecks.isRequired(() => this.state.password),
                errorMessage: translator(messages.passwordError)
            }
        ];
    }

    usernameChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ username: event.target.value })

    passwordChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ password: event.target.value })

    login = () => {
        this.props.resetLoginErrors();

        if (!this.props.isLoading) {
            const { username, password } = this.state;

            if (this.validator.isStateValid()) {
                this.props.login(username, password);
            }
        }

        this.setState({ showErrors: true });
    }

    render(): JSX.Element {
        const { password, username } = this.state;
        const { showLoginHeader, introText, forgotPasswordLinkPlacement, underFormContent,
            isLoading, intl, requestError } = this.props;

        const validationErrors = this.validator.getDisplayedErrors(this.state.showErrors, requestError);

        return (
            <Form
                submit={this.login}
                header={showLoginHeader
                    ? <FormattedMessage id={'web.components.auth.loginForm.header'} defaultMessage={'LOGIN'} />
                    : <div />}
                formClassName={loginPageFormStyle.LoginPageForm}
            >
                {introText && <FormattedMessage {...introText} />}
                {forgotPasswordLinkPlacement === ForgotPasswordLinkPlacement.Top && this.renderForgotPasswordLink()}
                <InputWithValidation
                    type="email"
                    name="username"
                    id="username"
                    value={username}
                    autoComplete="username"
                    placeholder={intl.formatMessage(messages.emailPlaceholder)}
                    onChange={this.usernameChanged}
                    validationErrors={validationErrors}
                    autoFocus={true}
                />
                <PasswordInputWithValidation
                    name="password"
                    id="password"
                    value={password}
                    autoComplete="current-password"
                    placeholder={intl.formatMessage(messages.passwordPlaceholder)}
                    onChange={this.passwordChanged}
                    validationErrors={validationErrors}
                />
                {forgotPasswordLinkPlacement === ForgotPasswordLinkPlacement.Bottom && this.renderForgotPasswordLink()}
                <RequestErrorMessage requestState={this.props} />
                <ButtonWithLoadingIndicator isLoading={isLoading}>
                    <FormattedMessage id={'web.components.auth.loginForm.logInButton'} defaultMessage={'LOG IN'} />
                </ButtonWithLoadingIndicator>
                {this.props.showContinueAsGuest && this.renderContinueAsGuest()}
                {underFormContent && <div className={formStyle.LineSeparator} />}
                {underFormContent}
            </Form>
        );
    }

    private renderForgotPasswordLink(): React.ReactNode {
        return (
            <Link
                to={{
                    pathname: '/forgotten-password',
                    search: `returnTo=${encodeLocationForRedirect(this.props.location)}`
                }}
                className={formStyle.TextLink}
            >
                <FormattedMessage
                    id={'components.auth.loginForm.forgottenPassword'}
                    defaultMessage={'Forgot password?'}
                />
            </Link>
        );
    }

    private renderContinueAsGuest(): React.ReactNode {
        return (
            <>
                <FormHeader>
                    <FormattedMessage
                        id={'components.auth.loginForm.continueAsGuest.title'}
                        defaultMessage={'CONTINUE AS GUEST'}
                    />
                </FormHeader>
                {ENABLE_CREATE_ACCOUNT ? (
                    <>
                        <FormattedMessage
                            id={'components.auth.loginForm.continueAsGuest.description1'}
                            defaultMessage={'Don\'t have time to make an account?'}
                        />
                        <FormattedMessage
                            id={'components.auth.loginForm.continueAsGuest.description2'}
                            defaultMessage={'No problem, you can still view all your documentation for a limited ' +
                                'time without registering and make an account at a later date. To re-review your ' +
                                'documentation you will need to click through from the email again.'}
                        />
                    </>
                ) : (
                    <>
                        <FormattedMessage
                            id={'components.auth.loginForm.continueAsGuest.noCreateAccount.description1'}
                            defaultMessage={'Don\'t have an account?'}
                        />
                        <FormattedMessage
                            id={'components.auth.loginForm.continueAsGuest.noCreateAccount.description2'}
                            defaultMessage={'No problem, you can still view all your documentation for a limited ' +
                            'time. To re-review your documentation you will need to click through from the email ' +
                            'again.'}
                        />
                    </>
                )}
                <button onClick={this.props.loginAsGuest}>
                    <FormattedMessage
                        id={'components.auth.loginForm.continueAsGuest.button'}
                        defaultMessage={'CONTINUE AS GUEST'}
                    />
                </button>
            </>
        );
    }
}

function mapStateToProps(
    state: AppState, ownProps: {}, getRequestError: (request: RequestClass<any>) => RequestStateProps
): LoginFormStateProps {
    return {
        ...getRequestError(PasswordTokenRequest)
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: LoginFormOwnProps): LoginFormDispatchProps {
    return {
        login: (username: string, password: string) =>
            dispatch(new FetchRequest(new PasswordTokenRequest(username, password, ownProps.loginPageVariant))),
        loginAsGuest: () => dispatch(new ContinueAsGuest()),
        resetLoginErrors: () => dispatch(new FetchResetStatus(PasswordTokenRequest))
    };
}

export const LoginForm = connectWithRequests(mapStateToProps, mapDispatchToProps)
    (injectIntl(LoginFormPresentational));
